import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import BrandLogo from "../components/BrandLogo";
import { toast, Toaster } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmail, setShowEmail] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Toaster position="top-right" richColors />
      {/* Left pane */}
      <div className="hidden md:flex flex-col justify-between p-12 bg-zinc-50 border-r border-zinc-200 grid-bg relative">
        <Link to="/"><BrandLogo /></Link>
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Secure Fulfillment</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
            Ship smarter.<br/>Bill cleaner.<br/>Scale faster.
          </h2>
          <p className="text-zinc-600 mt-6 max-w-md">
            Multi-carrier rate shopping, wallet-based billing, surcharge rebilling — one secure platform.
          </p>
        </div>
        <div className="font-mono-plex text-xs text-zinc-500">v1.0 · status: <span className="text-emerald-600">operational</span></div>
      </div>

      {/* Right pane */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-black tracking-tighter mb-2">Sign in to SellePack</h1>
          <p className="text-sm text-zinc-600 mb-8">
            New here? <Link to="/register" className="brand-text font-medium">Create an account</Link>
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Email</label>
              <div className="relative mt-1">
                <input
                  data-testid="login-email-input"
                  type={showEmail ? "email" : "password"}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 pr-10 focus:outline-none focus:border-[var(--brand-accent)]"
                />
                <button
                  type="button"
                  data-testid="toggle-email-visibility"
                  onClick={() => setShowEmail((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                  title={showEmail ? "Hide email" : "Show email"}
                >
                  {showEmail ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Password</label>
              <div className="relative mt-1">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 pr-10 focus:outline-none focus:border-[var(--brand-accent)]"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full brand-bg text-white py-2.5 rounded-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-zinc-400">
            <div className="flex-1 h-px bg-zinc-200" />
            <span>OR</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <button
            data-testid="login-google-button"
            onClick={googleLogin}
            className="w-full border border-zinc-300 rounded-sm py-2.5 flex items-center justify-center gap-3 hover:bg-zinc-50 text-sm font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2c-.4.4 6.7-4.9 6.7-14.9 0-1.3-.1-2.6-.5-3.9z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-xs text-zinc-500 mt-6 text-center">
            By signing in you agree to our <Link to="/legal/terms" className="brand-text hover:underline">Wholesale Terms</Link> and <Link to="/legal/privacy" className="brand-text hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
